import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Box, IconButton } from "@chakra-ui/react"
import { Switch } from "@hydrologiq/shared/chakra/Switch"
import { Tooltip } from "@hydrologiq/shared/chakra/Tooltip"
import { FaTrash } from "react-icons/fa6"
import { DataConnection } from "../../hooks/useDataConnections"
import { MapAssetDataProvider, useMapAssetDataState } from "../../providers/MapAssetDataProvider"
import { ConnectionMappingPanel } from "./ConnectionMappingPanel"
import { ConnectionStatus, COLOR_ACCENT, COLOR_SWITCH_INACTIVE, getConnectorConfig } from "@hydrologiq/shared/data-connections/constants"
import { BaseDataConnectionCard } from "@hydrologiq/shared/data-connections/BaseDataConnectionCard"
import { ManagedByChip } from "@hydrologiq/shared/data-connections/ManagedByChip"
import ConnectorActivationWorkflow from "@hydrologiq/shared/data-connections/ConnectorActivationWorkflow"
import { getDynamicStorageAssets, needsActivationConfig } from "@hydrologiq/shared/data-connections/activationUtils"
import type { AssetInfoEntry } from "../../utils/groupConnections"
import type { AssetVolumeConfig } from "@hydrologiq/shared/data-connections/activationUtils"

export interface DataConnectionCardProps {
  connection: DataConnection
  /** Heading in place of the connector name, e.g. the asset an asset-held connection belongs to */
  title?: ReactNode
  /** Chips beneath the title, e.g. the asset's scope */
  chips?: ReactNode
  /** Whether an asset-held connection says it is managed on the asset. Defaults to true; a group says it once for its modules. */
  showManagedBy?: boolean
  /** Whether the mapping panel starts expanded. Defaults to true. */
  defaultExpanded?: boolean
  onToggleStatus: (id: string, active: boolean, config?: { volume?: number | null; minPressure?: number | null }) => Promise<void>
  onRemove: (id: string) => Promise<void>
  isToggling: boolean
  isRemoving: boolean
  excludeAssetIds?: Set<string>
  onMappedAssetsChange?: (connectionId: string, assetIds: string[]) => void
  /** Asset metadata map (kind, volume, minPressure) used to determine dynamic_storage assets */
  assetInfoMap?: Record<string, AssetInfoEntry>
}

/**
 * A standalone data-connection card with full controls:
 * active/inactive toggle, delete button, and the mapping panel.
 * Used for ungrouped connections in the main connections list.
 */
export function DataConnectionCard({ connection, title, chips, showManagedBy, defaultExpanded, onToggleStatus, onRemove, isToggling, isRemoving, excludeAssetIds, onMappedAssetsChange, assetInfoMap }: DataConnectionCardProps) {
  return (
    <MapAssetDataProvider monitoringInstanceId={connection.id} excludeAssetIds={excludeAssetIds}>
      <DataConnectionCardContent
        connection={connection}
        title={title}
        chips={chips}
        showManagedBy={showManagedBy}
        defaultExpanded={defaultExpanded}
        onToggleStatus={onToggleStatus}
        onRemove={onRemove}
        isToggling={isToggling}
        isRemoving={isRemoving}
        onMappedAssetsChange={onMappedAssetsChange}
        assetInfoMap={assetInfoMap}
      />
    </MapAssetDataProvider>
  )
}

function DataConnectionCardContent({ connection, title, chips, showManagedBy = true, defaultExpanded, onToggleStatus, onRemove, isToggling, isRemoving, onMappedAssetsChange, assetInfoMap }: Omit<DataConnectionCardProps, "excludeAssetIds">) {
  const isActive = connection.status === ConnectionStatus.Active
  const { sources, mappedAssets, loadingSources, loadingMapped } = useMapAssetDataState()
  const mappingLoading = loadingSources || loadingMapped

  // Activation workflow state
  const [activationOpen, setActivationOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  // Report mapped asset IDs to parent for cross-connection filtering
  const mappedAssetIds = useMemo(() => mappedAssets.map((m) => m.assetId), [mappedAssets])
  useEffect(() => {
    onMappedAssetsChange?.(connection.id, mappedAssetIds)
  }, [connection.id, mappedAssetIds, onMappedAssetsChange])

  // Disable activation when sources exist but no mappings are configured
  const hasSources = sources.length > 0
  const hasMappings = mappedAssets.length > 0
  const activationBlocked = !mappingLoading && !isActive && hasSources && !hasMappings

  // Compute dynamic storage assets from mapped assets + assetInfoMap
  const dynamicStorageAssets = useMemo(() => {
    if (!assetInfoMap) return []
    const assetsWithInfo = mappedAssets.map((m) => ({
      id: m.assetId,
      assetId: m.assetId,
      name: m.assetName,
      assetName: m.assetName,
      kind: assetInfoMap[m.assetId]?.kind ?? null,
      volume: assetInfoMap[m.assetId]?.volume ?? null,
      minPressure: assetInfoMap[m.assetId]?.minPressure ?? null,
    }))
    return getDynamicStorageAssets(assetsWithInfo)
  }, [mappedAssets, assetInfoMap])

  // Determine connector config for requiresActivationConfig flag
  const connectorConfig = useMemo(
    () => getConnectorConfig(connection.connectorType),
    [connection.connectorType]
  )

  // Handle switch click — intercept activation for dynamic_storage assets
  const handleSwitchClick = useCallback(() => {
    if (activationBlocked) return

    if (isActive) {
      // Deactivating — proceed directly, no dialog
      onToggleStatus(connection.id, false)
    } else {
      // Activating — check if config dialog is needed
      const assetsWithKind = mappedAssets.map((m) => ({
        kind: assetInfoMap?.[m.assetId]?.kind ?? null,
      }))
      if (needsActivationConfig(connectorConfig.requiresActivationConfig, assetsWithKind)) {
        setActivationOpen(true)
      } else {
        // No dynamic_storage assets and no requiresActivationConfig — activate directly
        onToggleStatus(connection.id, true)
      }
    }
  }, [activationBlocked, isActive, connection.id, onToggleStatus, mappedAssets, assetInfoMap, connectorConfig])

  // Handle activation with volume/pressure config
  const handleActivateWithConfig = useCallback(async (assetConfigs: AssetVolumeConfig[]) => {
    // For a standalone connection, take the first config's values (single mapped asset case)
    const config = assetConfigs.length > 0 ? assetConfigs[0] : undefined
    await onToggleStatus(connection.id, true, config ? {
      volume: config.volume,
      minPressure: config.minPressure,
    } : undefined)
  }, [connection.id, onToggleStatus])

  // Handle deactivation (direct passthrough — no dialog)
  const handleDeactivate = useCallback(async () => {
    await onToggleStatus(connection.id, false)
  }, [connection.id, onToggleStatus])

  const actions = (
    <>
      {!isActive && (
        <Tooltip
          content="Connection must be inactive in order to delete"
          disabled={!isActive}
          positioning={{ placement: "top" }}
        >
          <Box display="inline-flex">
            <IconButton
              aria-label="Remove connection"
              variant={"hydrologiq_icon" as any}
              size="sm"
              disabled={isRemoving || isToggling}
              onClick={() => onRemove(connection.id)}
            >
              <FaTrash />
            </IconButton>
          </Box>
        </Tooltip>
      )}
      <Tooltip
        content="Map at least one asset to a source before activating"
        disabled={!activationBlocked}
        positioning={{ placement: "top" }}
      >
        <Box display="inline-flex">
          <Switch
            checked={isActive}
            disabled={isToggling || isRemoving || mappingLoading || activationBlocked}
            onClick={(e) => {
              e.preventDefault()
              handleSwitchClick()
            }}
            inputProps={{
              "aria-label": isActive ? "Deactivate connection" : "Activate connection",
            }}
            css={{
              "& [data-part='control']": {
                backgroundColor: isActive ? COLOR_ACCENT : COLOR_SWITCH_INACTIVE,
                transition: "background-color 0.2s ease",
              },
              "& [data-part='control'][data-state='checked']": {
                backgroundColor: COLOR_ACCENT,
              },
            }}
          />
        </Box>
      </Tooltip>
    </>
  )

  // Asset-held connections deployed into this system are managed from the asset
  // side. Render them read-only here: no activate/deactivate toggle, no delete,
  // and a non-editable mapping panel. We surface the connection status and a
  // link back to the owning asset's settings, where it is the only place the
  // connection can be managed.
  if (connection.assetOwned) {
    return (
      <Box ref={containerRef}>
        <BaseDataConnectionCard
          connection={connection}
          title={title}
          chips={chips}
          defaultExpanded={defaultExpanded}
          actions={null}
          headerNote={showManagedBy && <ManagedByChip scope="asset" />}
        >
          <ConnectionMappingPanel isEditable={false} managedOnAsset />
        </BaseDataConnectionCard>
      </Box>
    )
  }

  return (
    <Box ref={containerRef}>
      <BaseDataConnectionCard connection={connection} title={title} chips={chips} defaultExpanded={defaultExpanded} actions={actions}>
        <ConnectionMappingPanel isEditable={!isActive} />
      </BaseDataConnectionCard>

      <ConnectorActivationWorkflow
        open={activationOpen}
        action={activationOpen ? "activate" : null}
        dynamicStorageAssets={dynamicStorageAssets}
        onActivate={handleActivateWithConfig}
        onDeactivate={handleDeactivate}
        onClose={() => setActivationOpen(false)}
        onSuccess={() => setActivationOpen(false)}
        onError={() => {}}
        container={containerRef}
      />
    </Box>
  )
}

export default DataConnectionCard
